<template>  <v-card align="center" justify="center" min-width="50vw" max-width="50vw" max-height="85vh" outlined>
    <v-card-title>
      <v-list-item>
        <v-list-item-title class="mt-5 text-left">
          <h1>{{ $t('title_TournamentSettings') }}</h1>
          <p class="settings-description">{{ $t('settingsDescription') || 'Configure your tournament settings and customize the appearance' }}</p>
        </v-list-item-title>
        <v-spacer />
        <v-list-item-action style="align-self: flex-start;">
          <v-icon class="icon" icon @click="close">fa-times</v-icon>
        </v-list-item-action>
      </v-list-item>
    </v-card-title>
    <v-card-text class="px-3 py-4 scrollable-content">
      <v-container fluid>
        <div class="custom-tabs">
          <div class="tabs" :style="{ '--primary-color': colors.primary }">
            <input
              type="radio"
              id="tab-basic"
              :value="0"
              v-model="currentTab" />
            <label
              class="tab"
              for="tab-basic">
              <v-icon small>fa-regular fa-cog</v-icon>
              <span class="tab-label">{{ $t('basicSettings') }}</span>
            </label>

            <input
              type="radio"
              id="tab-customization"
              :value="1"
              v-model="currentTab" />
            <label
              class="tab"
              for="tab-customization">
              <v-icon small>fa-regular fa-palette</v-icon>
              <span class="tab-label">{{ $t('customization') }}</span>
            </label>

            <span class="glider"></span>
          </div>
        </div>

        <v-tabs-items v-model="currentTab">
          <!-- Basic Settings Tab -->
          <v-tab-item>
            <h3 class="section-title">{{ $t('basicSettings') }}</h3>
            <v-form v-model="valid" class="mt-4">
              <!-- Name and Dates Row -->
              <div class="d-flex align-center justify-center mb-3">
                <v-text-field :label="$t('nameLabel')"
                  :placeholder="$t('namePlaceholder', { name: this.$store.state.clubMember?.club?.name })"
                  v-model="tournamentName" :rules="tournamentNameRules" counter="45" outlined 
                  :disabled="isTournamentStarted"
                  :hint="isTournamentStarted ? $t('cannotEditAfterStart') : ''"
                  :persistent-hint="isTournamentStarted" />
                <v-menu :close-on-content-click="false" offset-y transition="scale-transition" v-model="menuDates"
                  :disabled="isTournamentStarted">
                  <template v-slot:activator="{ on }">
                    <v-text-field class="ml-2" :label="$t('datesLabel')" outlined readonly
                      v-model="datesRangeText" :rules="dateRules" v-on="on" 
                      :disabled="isTournamentStarted"
                      :hint="isTournamentStarted ? $t('cannotEditDatesAfterStart') : ''"
                      :persistent-hint="isTournamentStarted" />
                  </template>
                  <v-date-picker @input="inputDates" range no-title v-model="tournamentDates" />
                </v-menu>
              </div>
              
              <!-- Time Fields Row -->
              <div class="d-flex align-center justify-center mb-3">
                <v-tooltip 
                  bottom 
                  max-width="300"
                  color="var(--v-mainGreen-base)"
                  content-class="utc-tooltip-content"
                  nudge-bottom="-10"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field 
                      v-bind="attrs"
                      v-on="on"
                      :label="$t('startTime')"
                      v-model="startTime"
                      :rules="timeRules"
                      outlined
                      :disabled="isTournamentStarted"
                      :hint="$t('timeFormatHint')"
                      persistent-hint
                      placeholder="09:00"
                      class="mr-2"
                    >
                      <template v-slot:append>
                        <v-icon small color="mainGreen">fa-info-circle</v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <span class="white--text">{{ $t('utcTooltip') }}</span>
                </v-tooltip>
                
                <v-tooltip 
                  bottom 
                  max-width="300"
                  color="var(--v-mainGreen-base)"
                  content-class="utc-tooltip-content"
                  nudge-bottom="-10"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field 
                      v-bind="attrs"
                      v-on="on"
                      :label="$t('endTime')"
                      v-model="endTime"
                      :rules="timeRules"
                      outlined
                      :disabled="false"
                      :hint="$t('timeFormatHint')"
                      persistent-hint
                      placeholder="18:00"
                      class="ml-2"
                    >
                      <template v-slot:append>
                        <v-icon small color="mainGreen">fa-info-circle</v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <span class="white--text">{{ $t('utcTooltip') }}</span>
                </v-tooltip>
              </div>
              <v-checkbox v-model="publicPerformance" :label="$t('publicPerformance')" outlined/>
              <v-text-field :label="$t('faqsLabel')"
                :placeholder="$t('faqsPlaceholder', { name: String(this.$store.state.clubMember?.club?.name).replaceAll(' ', '').toLowerCase() })"
                :hint="$t('faqsHint')" persistent-hint v-model="tournamentFAQs" :rules="tournamentFAQsRules" counter="100"
                outlined />
            </v-form>
          </v-tab-item>          <!-- Customization Tab -->
          <v-tab-item>
            <h3 class="section-title">{{ $t('customization') }}</h3>
            <div class="customization-container">
              <!-- Partner Logo Section -->
              <v-card class="customization-card mb-4" elevation="0">
                <v-card-title class="customization-card-title">
                  <v-icon class="mr-3">fa-image</v-icon>
                  {{ $t('partnerLogo') }}
                </v-card-title>
                <v-card-text>
                  <div class="logo-upload-area">
                    <div class="upload-dropzone">
                      <ImageInput v-model="tournamentImage" @input="handleImageChange">
                        <div slot="activator" class="upload-content">
                          <!-- Show preview if exists -->
                          <div v-if="imagePreview" class="preview-section">
                            <div class="preview-image-container">
                              <v-img
                                :src="imagePreview"
                                max-height="180"
                                max-width="280"
                                class="preview-image-enhanced"
                                contain
                              ></v-img>
                              <div class="preview-badge">
                                <v-icon small color="white" class="mr-1">fa-check-circle</v-icon>
                                <span>{{ $t('currentLogoLabel') }}</span>
                              </div>
                            </div>
                            <v-divider class="my-4"></v-divider>
                          </div>

                          <!-- Upload UI -->
                          <v-icon size="48" class="upload-icon">
                            {{ imagePreview ? 'fa-sync' : 'fa-cloud-upload-alt' }}
                          </v-icon>
                          <h4 class="upload-title">
                            {{ imagePreview ? $t('changeImage') : $t('uploadImage') }}
                          </h4>
                          <p class="upload-subtitle">
                            {{ imagePreview ? $t('clickToReplaceLogoText') : $t('dragDropSelectText') }}
                          </p>
                          <v-btn
                            color="primary"
                            outlined
                            class="upload-btn"
                          >
                            <v-icon class="mr-2">fa-folder-open</v-icon>
                            {{ $t('browseFilesButton') }}
                          </v-btn>

                          <!-- Remove button -->
                          <v-btn
                            v-if="imagePreview"
                            @click.stop="removeImage"
                            color="error"
                            text
                            small
                            class="mt-3"
                          >
                            <v-icon small class="mr-1">fa-trash</v-icon>
                            {{ $t('remove') }}
                          </v-btn>
                        </div>
                      </ImageInput>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Color Theme Section -->
              <v-card class="customization-card mb-4" elevation="0">
                <v-card-title class="customization-card-title">
                  <v-icon class="mr-3">fa-palette</v-icon>
                  {{ $t('colorConfiguration') }}
                </v-card-title>
                <v-card-text>                  <p class="color-description mt-4">
                    {{ $t('colorThemeDescription') }}
                  </p>
                  
                  <v-row class="color-picker-row">
                    <v-col cols="12" md="6">
                      <div class="color-picker-item" @click="openColorPicker('primary')">
                        <div class="color-picker-content">
                          <div class="color-circle-large" :style="{ backgroundColor: colors.primary }">
                            <v-icon color="white" size="16">fa-edit</v-icon>
                          </div>
                          <div class="color-info">
                            <h4 class="color-label">{{ $t('primaryColor') }}</h4>
                            <p class="color-value">{{ colors.primary }}</p>
                            <small class="color-usage">{{ $t('primaryColorUsage') }}</small>
                          </div>
                        </div>
                        <v-icon class="color-edit-icon">fa-chevron-right</v-icon>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <div class="color-picker-item" @click="openColorPicker('secondary')">
                        <div class="color-picker-content">
                          <div class="color-circle-large" :style="{ backgroundColor: colors.secondary }">
                            <v-icon color="rgba(0,0,0,0.6)" size="16">fa-edit</v-icon>
                          </div>
                          <div class="color-info">
                            <h4 class="color-label">{{ $t('secondaryColor') }}</h4>
                            <p class="color-value">{{ colors.secondary }}</p>
                            <small class="color-usage">{{ $t('secondaryColorUsage') }}</small>
                          </div>
                        </div>
                        <v-icon class="color-edit-icon">fa-chevron-right</v-icon>
                      </div>
                    </v-col>
                  </v-row>
                    <!-- Color Preview Section -->
                  <div class="color-preview-section mt-4">
                    <h4 class="preview-title mb-3">{{ $t('colorPreviewTitle') }}</h4>
                    <div class="preview-sample">
                      <div class="sample-header" :style="{ backgroundColor: colors.primary }">
                        <span class="sample-text">{{ $t('tournamentHeaderSample') }}</span>
                      </div>
                      <div class="sample-content" :style="{ backgroundColor: colors.secondary }">                        <div class="sample-button" :style="{ backgroundColor: colors.primary }">
                          {{ $t('primaryButtonSample') }}
                        </div>                        <p class="sample-description">
                          {{ $t('colorPreviewDescription') }}
                        </p>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- NEW: Ranking Layout Customization Section -->
              <v-card class="customization-card" elevation="0">
                <v-card-title class="customization-card-title">
                  <v-icon class="mr-3">fa-table-columns</v-icon>
                  {{ $t('rankingLayout_customization') }}
                </v-card-title>
                <v-card-text>
                  <p class="layout-description mt-4">
                    {{ $t('rankingLayout_description') }}
                  </p>
                  
                  <v-row>
                    <!-- Left Column: Feature Selection -->
                    <v-col cols="12" md="6">
                      <h4 class="feature-selection-title mb-3">{{ $t('rankingLayout_selectFeatures') }}</h4>
                      
                      <!-- Feature Categories -->
                      <div class="feature-categories">
                        <!-- Page Components -->
                        <div class="feature-category mb-4">
                          <h5 class="category-title">{{ $t('rankingLayout_pageComponents') }}</h5>
                          <div class="feature-list">
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_COUNTER"
                              :label="$t('rankingLayout_tournamentTimer')"
                              class="feature-checkbox"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_AWARD"
                              :label="$t('rankingLayout_awardsCard')"
                              class="feature-checkbox"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_SPONSORED"
                              :label="$t('rankingLayout_sponsorCard')"
                              class="feature-checkbox"
                            ></v-checkbox>
                          </div>
                        </div>

                        <!-- Table Columns -->
                        <div class="feature-category mb-4">
                          <h5 class="category-title">{{ $t('rankingLayout_tableColumns') }}</h5>
                          <div class="feature-list">
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_NAME"
                              :label="$t('rankingLayout_participantName')"
                              class="feature-checkbox"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_USERNAME"
                              :label="$t('rankingLayout_username')"
                              class="feature-checkbox"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER"
                              :label="$t('rankingLayout_accountIdentifier')"
                              class="feature-checkbox"
                            ></v-checkbox>
                          </div>
                        </div>

                        <!-- Actions & Navigation -->
                        <div class="feature-category">
                          <h5 class="category-title">{{ $t('rankingLayout_actionsNavigation') }}</h5>
                          <div class="feature-list">
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_RULES"
                              :label="$t('rankingLayout_rulesButton')"
                              class="feature-checkbox"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_FAQS"
                              :label="$t('rankingLayout_faqsButton')"
                              class="feature-checkbox"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="selectedFeatures"
                              value="TOURNAMENT_RANKING_FULL_PAGINATION"
                              :label="$t('rankingLayout_tablePagination')"
                              class="feature-checkbox"
                            ></v-checkbox>
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Right Column: Live Preview -->
                    <v-col cols="12" md="6">
                      <h4 class="preview-title mb-3">{{ $t('rankingLayout_livePreview') }}</h4>
                      
                      <div class="layout-preview">
                        <!-- Preview Container -->
                        <div class="preview-container">
                          <!-- Header (Always visible) -->
                          <div class="preview-header">
                            <div class="preview-header-main">
                              <div class="preview-header-left">
                                <div class="preview-status-chip">{{ $t('rankingLayout_sampleStatus') }}</div>
                                <div class="preview-title-text">{{ $t('rankingLayout_sampleTournamentName') }}</div>
                                <div class="preview-organizer">{{ $t('rankingLayout_sampleOrganizer') }}</div>
                              </div>
                              
                              <!-- Timer (Conditional) - Now positioned on the right -->
                              <div 
                                v-if="selectedFeatures.includes('TOURNAMENT_RANKING_COUNTER')"
                                class="preview-timer-right"
                              >
                                <i class="fa-regular fa-clock mr-1"></i>
                                {{ $t('rankingLayout_sampleTimer') }}
                              </div>
                            </div>
                          </div>

                          <!-- Main Content Area -->
                          <div class="preview-main-content">
                            <!-- Left Side: Table -->
                            <div class="preview-table-container">
                              <div class="preview-table-header">
                                <span class="preview-table-title">{{ $t('rankingLayout_leaderboard') }}</span>
                              </div>
                              <div class="preview-table">
                                <!-- Table Headers -->
                                <div class="preview-table-row preview-table-header-row">
                                  <span class="preview-table-cell">{{ $t('rankingLayout_rank') }}</span>
                                  <span 
                                    v-if="selectedFeatures.includes('TOURNAMENT_RANKING_NAME')"
                                    class="preview-table-cell"
                                  >{{ $t('rankingLayout_name') }}</span>
                                  <span 
                                    v-if="selectedFeatures.includes('TOURNAMENT_RANKING_USERNAME')"
                                    class="preview-table-cell"
                                  >{{ $t('rankingLayout_username') }}</span>
                                  <span 
                                    v-if="selectedFeatures.includes('TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER')"
                                    class="preview-table-cell"
                                  >{{ $t('account_NewFrontPage') }}</span>
                                  <span class="preview-table-cell">{{ $t('rankingLayout_score') }}</span>
                                </div>
                                <!-- Sample Data Rows -->
                                <div class="preview-table-row" v-for="n in 3" :key="n">
                                  <span class="preview-table-cell">{{ n }}</span>
                                  <span 
                                    v-if="selectedFeatures.includes('TOURNAMENT_RANKING_NAME')"
                                    class="preview-table-cell"
                                  >{{ $t('rankingLayout_sampleName') }} {{ n }}</span>
                                  <span 
                                    v-if="selectedFeatures.includes('TOURNAMENT_RANKING_USERNAME')"
                                    class="preview-table-cell"
                                  >user{{ n }}</span>
                                  <span 
                                    v-if="selectedFeatures.includes('TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER')"
                                    class="preview-table-cell"
                                  >ACC{{ n }}{{ n }}{{ n }}</span>
                                  <span class="preview-table-cell">{{ (100 - n * 10).toFixed(2) }}%</span>
                                </div>
                              </div>
                              
                              <!-- Pagination (Conditional) - Now aligned to the right -->
                              <div 
                                v-if="selectedFeatures.includes('TOURNAMENT_RANKING_FULL_PAGINATION')"
                                class="preview-pagination"
                              >
                                <div class="preview-pagination-right">
                                  <button class="preview-pagination-btn">‹</button>
                                  <span class="preview-pagination-text">1/3</span>
                                  <button class="preview-pagination-btn">›</button>
                                </div>
                              </div>
                            </div>

                            <!-- Right Side: Cards -->
                            <div class="preview-sidebar">
                              <!-- User Summary (Always visible) -->
                              <div class="preview-card">
                                <div class="preview-card-title">{{ $t('rankingLayout_yourParticipation') }}</div>
                                <div class="preview-card-content">...</div>
                              </div>

                              <!-- Awards Card (Conditional) -->
                              <div 
                                v-if="selectedFeatures.includes('TOURNAMENT_RANKING_AWARD')"
                                class="preview-card"
                              >
                                <div class="preview-card-title">{{ $t('rankingLayout_awards') }}</div>
                                <div class="preview-card-content">
                                  <i class="fa-regular fa-trophy mr-1"></i>
                                  {{ $t('rankingLayout_sampleAwards') }}
                                </div>
                              </div>

                              <!-- Sponsor Card (Conditional) -->
                              <div 
                                v-if="selectedFeatures.includes('TOURNAMENT_RANKING_SPONSORED')"
                                class="preview-card"
                              >
                                <div class="preview-card-title">{{ $t('rankingLayout_sponsor') }}</div>
                                <div class="preview-card-content">
                                  <div class="preview-sponsor-image">{{ $t('rankingLayout_sponsorLogo') }}</div>
                                </div>
                              </div>

                              <!-- Action Buttons (Conditional) - Moved here below the cards -->
                              <div class="preview-actions" v-if="selectedFeatures.includes('TOURNAMENT_RANKING_RULES') || selectedFeatures.includes('TOURNAMENT_RANKING_FAQS')">
                                <button 
                                  v-if="selectedFeatures.includes('TOURNAMENT_RANKING_RULES')"
                                  class="preview-action-btn"
                                >
                                  <i class="fa-regular fa-list mr-1"></i>
                                  {{ $t('rankingLayout_viewRules') }}
                                </button>
                                <button 
                                  v-if="selectedFeatures.includes('TOURNAMENT_RANKING_FAQS')"
                                  class="preview-action-btn"
                                >
                                  <i class="fa-regular fa-circle-question mr-1"></i>
                                  {{ $t('rankingLayout_viewFAQs') }}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>
          </v-tab-item>
        </v-tabs-items>

        <v-row class="mt-7" align="center" justify="center">
          <v-btn @click="close">{{ $t('btnCancel_TournamentSettings') }}</v-btn>
          <v-btn :disabled="!this.valid || this.tournamentDates.length !== 2" class="ml-3" @click="editTournament">
            {{ $t('btnEdit') }}</v-btn>
        </v-row>
      </v-container>
    </v-card-text>

    <!-- Color Picker Overlay -->
    <v-overlay :dark="false" v-model="showColorPicker">
      <ColorPicker :initial-color="selectedColorValue" @saveColor="saveColor" @closeWindow="showColorPicker = false"/>
    </v-overlay>

    <!-- Success/Error Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="4000" :color="snackbarColor" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import Repository from '../../../repositories/tournament';
import { mapMutations } from 'vuex';
import ColorPicker from '../../../components/ColorPicker.vue';
import ImageInput from '../../../components/ImageInput.vue';

export default {
  name: "TournamentSettings",
  components: {
    ColorPicker,
    ImageInput
  },
  props: {
    tournamentSummary: Object,
  },
  data() {
    return {
      valid: false,
      currentTab: 0,
      tournamentName: '',
      tournamentFAQs: '',
      tournamentDates: [new Date().toISOString().substr(0, 10), new Date().toISOString().substr(0, 10)],
      menuDates: false,
      formalStartDate: null,
      formalEndDate: null,
      originalStartTime: null,  // Store original start time
      originalEndTime: null,    // Store original end time
      startTime: '09:00',       // Time input for start
      endTime: '18:00',         // Time input for end
      publicPerformance: false,
      
      // Image handling
      tournamentImage: null,
      imagePreview: null,
      uploadingImage: false,
      imageUploaded: false,
      
      // Color configuration
      colors: {
        primary: '#009F63',
        secondary: '#ffffff'
      },
      showColorPicker: false,
      selectedColorType: null,
      selectedColorValue: '',
        // Feedback
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      // Ranking layout customization
      selectedFeatures: [
        'TOURNAMENT_RANKING_COUNTER',
        'TOURNAMENT_RANKING_AWARD',
        'TOURNAMENT_RANKING_SPONSORED',
        'TOURNAMENT_RANKING_NAME',
        'TOURNAMENT_RANKING_USERNAME',
        'TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER',
        'TOURNAMENT_RANKING_RULES',
        'TOURNAMENT_RANKING_FAQS',
        'TOURNAMENT_RANKING_FULL_PAGINATION'
      ],
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    datesRangeText() {
      return this.tournamentDates.join(' to ');
    },
    tournamentNameRules() {
      return [
        t => !!t || this.$t('nameRequiredValidation'),
        t => (t && t.length <= 45) || this.$t('maxCharactersValidation', { max: 45 }),
      ];
    },
    dateRules() {
      return [
        d => !!d || this.$t('daysRequiredValidation'),
      ];
    },
    tournamentFAQsRules() {
      return [
        f => ((f && f.length <= 100) || f.length === 0) || this.$t('maxCharactersValidation', { max: 100 }),
      ];
    },
    timeRules() {
      return [
        t => !!t || this.$t('timeRequired'),
        t => this.isValidTimeFormat(t) || this.$t('invalidTimeFormat'),
      ];
    },
    isTournamentInProgress() {
      const now = new Date();
      const startTime = new Date(this.tournamentSummary.startTime);
      const endTime = new Date(this.tournamentSummary.endTime);
      return now >= startTime && now <= endTime;
    },
    isTournamentStarted() {
      const now = new Date();
      const startTime = new Date(this.tournamentSummary.startTime);
      return now >= startTime;
    }
  },
  methods: {
    init() {
      this.tournamentName = this.tournamentSummary.tournamentName;
      // Check for publicPerformance in tournamentSettings first, then in root object
      if (this.tournamentSummary.tournamentSettings && this.tournamentSummary.tournamentSettings.publicPerformance !== undefined) {
        this.publicPerformance = this.tournamentSummary.tournamentSettings.publicPerformance;
      } else if (this.tournamentSummary.publicPerformance !== undefined) {
        this.publicPerformance = this.tournamentSummary.publicPerformance;
      } else {
        this.publicPerformance = false;
      }
      // Check for faqsUrl in tournamentSettings first, then in root object
      if (this.tournamentSummary.tournamentSettings && this.tournamentSummary.tournamentSettings.faqsUrl !== undefined) {
        this.tournamentFAQs = this.tournamentSummary.tournamentSettings.faqsUrl || '';
      } else if (this.tournamentSummary.faqsUrl !== undefined) {
        this.tournamentFAQs = this.tournamentSummary.faqsUrl || '';
      } else {
        this.tournamentFAQs = '';
      }
      
      // Store original times to preserve them when updating
      this.originalStartTime = new Date(this.tournamentSummary.startTime);
      this.originalEndTime = new Date(this.tournamentSummary.endTime);
      
      // Extract time components for the time inputs (in HH:MM format)
      const formatTime = (date) => {
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      };
      
      this.startTime = formatTime(this.originalStartTime);
      this.endTime = formatTime(this.originalEndTime);
      
      // Extract only dates for the date picker
      this.tournamentDates = [new Date(this.tournamentSummary.startTime).toISOString().substr(0, 10), new Date(this.tournamentSummary.endTime).toISOString().substr(0, 10)];
      
      // Initialize image preview if imageUrl exists
      if (this.tournamentSummary.imageUrl) {
        this.imagePreview = this.tournamentSummary.imageUrl;
      }
      
      // Initialize colors if they exist (check tournamentSettings first, then root)
      if (this.tournamentSummary.tournamentSettings && this.tournamentSummary.tournamentSettings.colors) {
        this.colors = { ...this.colors, ...this.tournamentSummary.tournamentSettings.colors };
      } else if (this.tournamentSummary.colors) {
        this.colors = { ...this.colors, ...this.tournamentSummary.colors };
      }
      
      // Initialize selected features - if array is not present, check all features
      if (this.tournamentSummary.selectedFeatures && Array.isArray(this.tournamentSummary.selectedFeatures)) {
        this.selectedFeatures = [...this.tournamentSummary.selectedFeatures];
      } else {
        // If no selectedFeatures array, enable all features (backward compatibility)
        this.selectedFeatures = [
          'TOURNAMENT_RANKING_COUNTER',
          'TOURNAMENT_RANKING_AWARD',
          'TOURNAMENT_RANKING_SPONSORED',
          'TOURNAMENT_RANKING_NAME',
          'TOURNAMENT_RANKING_USERNAME',
          'TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER',
          'TOURNAMENT_RANKING_RULES',
          'TOURNAMENT_RANKING_FAQS',
          'TOURNAMENT_RANKING_FULL_PAGINATION'
        ];
      }
    },
    
    inputDates() {
      if (this.tournamentDates.length === 2)
        this.menuDates = false;
    },
    
    // Image handling methods
    handleImageChange(imageData) {
      this.tournamentImage = imageData;
      if (imageData && imageData.imageURL) {
        this.imagePreview = imageData.imageURL;
        this.imageUploaded = false; // Mark as not uploaded yet
      }
    },

    removeImage() {
      this.tournamentImage = null;
      this.imagePreview = null;
      this.imageUploaded = false;
    },

    uploadTournamentImage() {
      this.uploadingImage = true;
      let data = {
        formData: this.tournamentImage.formData,
        tournamentId: this.tournamentSummary.tournamentId
      };

      Repository.updateTournamentImage(data).then((response) => {
        // Backend returns the cloud URL where image was stored
        this.imagePreview = response.data;
        this.tournamentImage.imageURL = response.data;
        this.uploadingImage = false;
        this.imageUploaded = true;
      }).catch(() => {
        this.uploadingImage = false;
        this.showErrorMessage(this.$t('alerts.errorOccurred'));
      });
    },

    // Color picker methods
    openColorPicker(colorType) {
      this.selectedColorType = colorType;
      this.selectedColorValue = this.colors[colorType].replace('#', '');
      this.showColorPicker = true;
    },
    
    saveColor(color) {
      if (this.selectedColorType) {
        this.colors[this.selectedColorType] = color;
      }
      this.showColorPicker = false;
    },
    
    // Feedback methods
    showSuccessMessage(message) {
      this.snackbarText = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    
    showErrorMessage(message) {
      this.snackbarText = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
    
    ...mapMutations(['setTournament']),
    
    isValidTimeFormat(time) {
      if (!time) return false;
      const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      return timePattern.test(time);
    },
    
    editTournament() {
      if (this.valid && this.tournamentDates.length === 2 && this.isValidTimeFormat(this.startTime) && this.isValidTimeFormat(this.endTime)) {
        // Create dates with user-specified times (treating as UTC)
        this.formalStartDate = new Date(`${this.tournamentDates[0]}T${this.startTime}:00.000Z`);
        this.formalEndDate = new Date(`${this.tournamentDates[1]}T${this.endTime}:00.000Z`);

        // Allow editing end date even after tournament starts, but validate it's not before current time if tournament is in progress
        if (this.isTournamentInProgress && this.formalEndDate <= new Date()) {
          this.showErrorMessage(this.$t('alerts.endDateCannotBeInPast'));
          return false;
        }

        // Only validate start date if tournament hasn't started yet
        if (!this.isTournamentStarted) {
          if (this.formalStartDate >= this.formalEndDate) {
            this.showErrorMessage(this.$t('alerts_TournamentSettings.notBefore'));
            return false;
          }
          else if (new Date() > this.formalStartDate) {
            this.showErrorMessage(this.$t('alerts_TournamentSettings.notToday'));
            return false;
          }
        } else {
          // If tournament has started, keep original start date with time
          this.formalStartDate = new Date(this.tournamentSummary.startTime);
        }

        // Validate end date is after start date
        if (this.formalStartDate >= this.formalEndDate) {
          this.showErrorMessage(this.$t('alerts_TournamentSettings.notBefore'));
          return false;
        }

        // If there's a new image with formData, upload it to cloud first
        if (this.tournamentImage && this.tournamentImage.formData && !this.imageUploaded) {
          this.uploadTournamentImage();
          // Wait for cloud upload to complete, then save tournament
          const uploadCheckInterval = setInterval(() => {
            if (!this.uploadingImage) {
              clearInterval(uploadCheckInterval);
              if (this.imageUploaded) {
                this.saveTournamentSettings();
              }
            }
          }, 100);
        } else {
          // No new image or already uploaded, proceed with save
          this.saveTournamentSettings();
        }
      }
    },

    saveTournamentSettings() {
      let tournamentEditData = {
        tournamentId: this.tournamentSummary.tournamentId,
        tournamentName: this.isTournamentStarted ? this.tournamentSummary.tournamentName : this.tournamentName,
        startTime: this.formalStartDate.toISOString(),
        endTime: this.formalEndDate.toISOString(),
        faqsUrl: this.tournamentFAQs,
        publicPerformance: this.publicPerformance,
        colors: this.colors,
        imageUrl: this.imagePreview, // This will be the cloud URL after upload
        selectedFeatures: this.selectedFeatures
      };

      Repository.editFullTournament(tournamentEditData).then((response) => {
        if (response.status < 400 && response.data) {
          this.showSuccessMessage(this.$t('alerts_TournamentSettings.edited'));
          Repository.getTournamentOverview(this.tournamentSummary.tournamentId).then((response) => {
            if (response.status < 400)
              this.setTournament(response.data);
          });
          setTimeout(() => {
            this.close();
          }, 2000);
        }
        else {
          this.showErrorMessage(this.$t('alerts.errorOccurred'));
        }
      }).catch(() => {
        this.showErrorMessage(this.$t('alerts.errorOccurred'));
      });
    },
    
    capitalize(string) {
      return string[0].toUpperCase() + string.slice(1).toLowerCase();
    },
    
    close() {
      this.$emit('closeWindow');
    },
  }
}
</script>



<style scoped>
.v-card {
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.scrollable-content {
  max-height: calc(85vh - 140px);
  overflow-y: auto;
  overflow-x: hidden;
}

.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.theme--dark .scrollable-content::-webkit-scrollbar-track {
  background: #2a2a2a;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.theme--dark .scrollable-content::-webkit-scrollbar-thumb {
  background: #555;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.theme--dark .scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #777;
}

.v-card-title h2 {
  font-weight: 600;
  font-size: 1.5rem;
}

.v-card-title .icon {
  cursor: pointer;
  transition: color 0.2s ease;
}

.v-card-title .icon:hover {
  color: #ffeb3b;
}

/* Custom Tabs Styling */
.custom-tabs {
  width: 100%;
  margin-bottom: 24px;
  display: flex;
  justify-content: flex-start;

  .tabs {
    display: flex;
    position: relative;
    background-color: var(--v-lightGray-base);
    border-radius: 10px;

    * {
      z-index: 2;
    }

    input[type="radio"] {
      display: none;

      &:checked {
        & + label {
          color: white !important;

          .v-icon {
            color: white !important;
          }
        }
      }
    }

    input[id="tab-basic"] {
      &:checked {
        & ~ .glider {
          transform: translateX(0);
        }
      }
    }

    input[id="tab-customization"] {
      &:checked {
        & ~ .glider {
          transform: translateX(100%);
        }
      }
    }

    .tab {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 140px;
      height: 38px;
      font-size: 14px;
      font-weight: 600;
      color: var(--v-mainBlack-base);
      border-radius: 10px;
      cursor: pointer;
      transition: color 0.15s ease-in;
      font-family: "Avenir", sans-serif;

      .v-icon {
        margin-right: 8px;
        font-size: 14px;
        color: var(--v-mainBlack-base);
        transition: color 0.15s ease-in;
      }

      .tab-label {
        white-space: nowrap;
      }
    }

    .glider {
      position: absolute;
      display: flex;
      height: 38px;
      width: 140px;
      background-color: var(--primary-color, var(--v-mainGreen-base));
      border-radius: 10px;
      z-index: 1;
      transition: 0.25s ease-out;
    }
  }
}

.settings-description {
  font-size: 14px;
  color: var(--v-darkGray-base);
  margin-top: 8px;
  margin-bottom: 0;
  line-height: 1.2;
  font-weight: normal;
}

.section-title {
  text-align: left;
  font-size: 18px;
  font-weight: 600;
  color: var(--v-mainBlack-base);
  margin-top: 0;
  margin-bottom: 0;
  font-family: "Avenir", sans-serif;
}

.theme--dark .settings-description {
  color: var(--v-lightGray-base);
}

.theme--dark .section-title {
  color: var(--v-fullWhite-base);
}

.theme--dark .custom-tabs {
  .tabs {
    background-color: #303740;

    .tab {
      color: var(--v-fullWhite-base);

      .v-icon {
        color: var(--v-fullWhite-base);
      }

      &:hover {
        opacity: 0.8;
      }
    }
  }
}

.v-tab-item {
  padding: 0 16px;
}

/* Image Upload Section Styling */
.preview-image {
  border-radius: 8px;
  border: 1px solid #9A9A9A;
}

.theme--dark .preview-image {
  border-color: #9A9A9A;
}

/* Enhanced Customization Tab Styling */
.customization-container {
  padding: 16px 0;
}

.customization-card {
  border-radius: 15px;
  border: 1px solid #9A9A9A;
  background-color: var(--v-fullWhite-base);
}

.theme--dark .customization-card {
  border-color: #9A9A9A;
  background-color: var(--v-cardBackground-base);
}

.customization-card-title {
  background-color: transparent;
  color: var(--v-mainBlack-base);
  font-weight: 600;
  padding: 16px 20px;
  border-radius: 15px 15px 0 0;
  font-size: 16px;
}

.theme--dark .customization-card-title {
  background-color: transparent;
  color: var(--v-fullWhite-base);
}

.customization-card-title .v-icon {
  color: var(--v-mainBlack-base);
}

.theme--dark .customization-card-title .v-icon {
  color: var(--v-fullWhite-base);
}

/* Logo Upload Area */
.logo-upload-area {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.current-logo-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.preview-container {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 2px dashed #e0e0e0;
  padding: 16px;
  background: #fafafa;
}

.theme--dark .preview-container {
  border-color: #424242;
  background: #1a1a1a;
}

.preview-image-enhanced {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.preview-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.preview-container:hover .preview-overlay {
  opacity: 1;
}

.preview-label {
  font-size: 14px;
  color: #6c757d;
  margin: 0;
  font-weight: 500;
}

.theme--dark .preview-label {
  color: #adb5bd;
}

.upload-dropzone {
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  background: linear-gradient(135deg, var(--v-lightGray-base) 0%, var(--v-fullWhite-base) 100%);
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme--dark .upload-dropzone {
  border-color: #424242;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
}

.upload-dropzone:hover {
  border-color: #009f63;
  background: linear-gradient(135deg, #f0fff4 0%, #f8f9fa 100%);
  transform: translateY(-2px);
}

.theme--dark .upload-dropzone:hover {
  border-color: #009f63;
  background: linear-gradient(135deg, #0a2a1a 0%, #1a1a1a 100%);
}

.upload-dropzone.has-image {
  min-height: 100px;
  padding: 20px;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-icon {
  opacity: 1;
  color: var(--v-mainBlack-base);
  transition: all 0.3s ease;
}

.upload-dropzone:hover .upload-icon {
  opacity: 1;
  transform: scale(1.1);
}

.upload-title {
  margin: 0;
  font-weight: 600;
  font-size: 1.2rem;
}

.theme--dark .upload-title {
  color: var(--v-mainWhite-base);
}

.upload-subtitle {
  margin: 0;
  font-size: 14px;
}


.upload-btn {
  margin-top: 8px;
}

/* Color Configuration Styling */
.color-description {
  color: #6c757d;
  font-size: 14px;
  line-height: 1.5;
}

.theme--dark .color-description {
  color: #adb5bd;
}

.color-picker-row {
  margin: 0 -8px;
}

.color-picker-item {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: 20px;
  border: 2px solid #e9ecef;
  box-shadow: 0 4px 16px rgba(0, 159, 99, 0.15);
}

.theme--dark .color-picker-item:hover {
  background: linear-gradient(135deg, #0a2a1a 0%, var(--v-cardBackground-base) 100%);
  box-shadow: 0 4px 16px rgba(0, 159, 99, 0.25);
}

.color-picker-content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-height: 100px;
}

.color-circle-large {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
}

.theme--dark .color-circle-large {
  border-color: #424242;
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
}

.color-picker-item:hover .color-circle-large {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.theme--dark .color-picker-item:hover .color-circle-large {
  box-shadow: 0 6px 20px rgba(255, 255, 255, 0.2);
}

.color-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 80px;
}

.color-label {
  color: #495057;
  margin: 0 0 4px 0;
  font-weight: 600;
  font-size: 1.1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme--dark .color-label {
  color: var(--v-mainWhite-base);
}

.color-value {
  color: #6c757d;
  margin: 0 0 4px 0;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme--dark .color-value {
  color: #adb5bd;
}

.color-usage {
  color: #adb5bd;
  font-size: 12px;
  line-height: 1.4;
  margin-top: auto;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.theme--dark .color-usage {
  color: #6c757d;
}

.color-edit-icon {
  color: #adb5bd;
  opacity: 0.6;
  transition: all 0.3s ease;
  align-self: center;
  flex-shrink: 0;
}

.theme--dark .color-edit-icon {
  color: #6c757d;
}

.color-picker-item:hover .color-edit-icon {
  opacity: 1;
  color: #009f63;
  transform: translateX(4px);
}

/* Color Preview Section */
.color-preview-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.theme--dark .color-preview-section {
  background: #1a1a1a;
  border-color: #424242;
}

.preview-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 12px;
}

.theme--dark .preview-title {
  color: var(--v-mainWhite-base);
}

.preview-sample {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: var(--v-fullWhite-base);
  max-width: 400px;
  margin: 0 auto;
}

.theme--dark .preview-sample {
  background: var(--v-cardBackground-base);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
}

.sample-header {
  padding: 12px 16px;
  color: #ffffff;
  font-weight: 600;
  text-align: center;
  font-size: 14px;
}

.sample-content {
  padding: 16px;
  min-height: 100px;
  background: var(--v-fullWhite-base);
}

.theme--dark .sample-content {
  background: var(--v-cardBackground-base);
}

.sample-button {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 6px;
  color: #ffffff;
  font-weight: 500;
  margin-bottom: 12px;
  text-align: center;
  font-size: 12px;
}

.sample-description {
  color: var(--v-mainBlack-base) !;
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  opacity: 0.7;
}

.theme--dark .sample-description {
  color: var(--v-mainWhite-base) !important;
  opacity: 0.7;
}

/* Section Headers */
h3 {
  color: var(--v-mainBlack-base);
  font-weight: 600;
  font-size: 1.2rem;
  padding-bottom: 8px;
  margin-bottom: 16px;
}

.theme--dark h3 {
  color: var(--v-mainWhite-base);
}

/* Button Styling */
.v-btn {
  border-radius: 8px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

/* Form Field Styling */
.v-text-field {
  margin-bottom: 8px;
}

.v-text-field--outlined .v-input__control .v-input__slot {
  border-radius: 8px;
}

/* Checkbox Styling */
.v-input--checkbox .v-input__control .v-input__slot {
  margin-bottom: 8px;
}

/* Date Picker Field */
.v-menu__content {
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.theme--dark .v-menu__content {
  box-shadow: 0 8px 32px rgba(255, 255, 255, 0.1);
}

/* Tabs Items Container */
.v-tabs-items {
  background: transparent;
}

.v-tab-item {
  min-height: 400px;
}

/* Image Upload Button */
.v-btn .v-icon {
  margin-right: 8px;
}

/* NEW: Ranking Layout Customization Styles */
.layout-description {
  color: #6c757d;
  font-size: 14px;
  line-height: 1.5;
}

.theme--dark .layout-description {
  color: #adb5bd;
}

.feature-selection-title,
.preview-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 12px;
}

.theme--dark .feature-selection-title,
.theme--dark .preview-title {
  color: var(--v-mainWhite-base);
}

/* Feature Categories */
.feature-categories {
  max-height: 400px;
  overflow-y: auto;
}

.feature-category {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  background: #f8f9fa;
}

.theme--dark .feature-category {
  border-color: #424242;
  background: #1a1a1a;
}

.category-title {
  color: #495057;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #dee2e6;
}

.theme--dark .category-title {
  color: var(--v-mainWhite-base);
  border-bottom-color: #424242;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.feature-checkbox {
  margin: 0;
  padding: 2px 0;
}

.feature-checkbox ::v-deep .v-input__control {
  min-height: 24px;
}

.feature-checkbox ::v-deep .v-label {
  font-size: 13px;
  line-height: 1.2;
}

/* Live Preview Styles */
.layout-preview {
  border: 2px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  max-height: 500px;
  overflow-y: auto;
}

.theme--dark .layout-preview {
  border-color: #424242;
  background: var(--v-cardBackground-base);
}

.preview-container {
  padding: 12px;
  font-size: 11px;
  transform: scale(0.85);
  transform-origin: top left;
  width: 118%; /* Compensate for scale */
  height: 118%; /* Compensate for scale */
}

/* Preview Header */
.preview-header {
  margin-bottom: 8px;
}

.preview-header-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.preview-header-left {
  flex: 1;
  text-align: left;
}

.preview-status-chip {
  display: inline-block;
  background: #009f63;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  margin-bottom: 4px;
}

.preview-title-text {
  font-size: 14px;
  font-weight: bold;
  color: var(--v-mainBlack-base);
  margin-bottom: 2px;
  text-align: left;
}

.theme--dark .preview-title-text {
  color: var(--v-mainBlack-base);
}

.preview-organizer {
  font-size: 10px;
  color: var(--v-mainBlack-base);
  opacity: 0.6;
  text-align: left;
}

.theme--dark .preview-organizer {
  color: var(--v-mainBlack-base);
  opacity: 0.7;
}

/* Preview Timer - Right positioned */
.preview-timer-right {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 8px;
  text-align: center;
  font-size: 10px;
  color: #495057;
  white-space: nowrap;
  flex-shrink: 0;
}

.theme--dark .preview-timer-right {
  background: #2a2a2a;
  border-color: #424242;
  color: var(--v-mainBlack-base);
}

/* Preview Main Content */
.preview-main-content {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

/* Preview Table */
.preview-table-container {
  flex: 2;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  background: var(--v-fullWhite-base);
}

.theme--dark .preview-table-container {
  border-color: #424242;
  background: var(--v-cardBackground-base);
}

.preview-table-header {
  background: #f8f9fa;
  padding: 4px 6px;
  font-size: 10px;
  font-weight: 600;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
}

.theme--dark .preview-table-header {
  background: #2a2a2a;
  border-bottom-color: #424242;
  color: var(--v-mainWhite-base);
}

.preview-table {
  font-size: 9px;
}

.preview-table-row {
  display: flex;
  border-bottom: 1px solid #f1f3f4;
  padding: 2px 0;
}

.theme--dark .preview-table-row {
  border-bottom-color: #3a3a3a;
}

.preview-table-header-row {
  background: #f8f9fa;
  font-weight: 600;
  padding: 3px 0;
}

.theme--dark .preview-table-header-row {
  background: #2a2a2a;
}

.preview-table-cell {
  flex: 1;
  padding: 2px 4px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #495057;
}

.theme--dark .preview-table-cell {
  color: var(--v-mainWhite-base);
}

/* Preview Pagination */
.preview-pagination {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  padding: 4px;
}

.theme--dark .preview-pagination {
  background: #2a2a2a;
  border-top-color: #424242;
}

.preview-pagination-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
}

.preview-pagination-btn {
  background: #009f63;
  color: white;
  border: none;
  border-radius: 3px;
  width: 16px;
  height: 16px;
  font-size: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-pagination-text {
  font-size: 8px;
  color: #495057;
}

.theme--dark .preview-pagination-text {
  color: var(--v-mainWhite-base);
}

/* Preview Sidebar */
.preview-sidebar {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.preview-card {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: var(--v-fullWhite-base);
  overflow: hidden;
}

.theme--dark .preview-card {
  border-color: #424242;
  background: var(--v-cardBackground-base);
}

.preview-card-title {
  background: #f8f9fa;
  padding: 4px 6px;
  font-size: 9px;
  font-weight: 600;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
}

.theme--dark .preview-card-title {
  background: #2a2a2a;
  border-bottom-color: #424242;
  color: var(--v-mainBlack-base);
}

.preview-card-content {
  padding: 6px;
  font-size: 8px;
  color: #6c757d;
  min-height: 20px;
  display: flex;
  align-items: center;
}

.theme--dark .preview-card-content {
  color: #adb5bd;
}

.preview-sponsor-image {
  background: #f1f3f4;
  border: 1px dashed #dee2e6;
  border-radius: 4px;
  padding: 8px;
  text-align: center;
  color: #6c757d;
  font-size: 8px;
}

.theme--dark .preview-sponsor-image {
  background: #3a3a3a;
  border-color: #424242;
  color: #adb5bd;
}

/* Preview Actions */
.preview-actions {
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 8px;
}

.preview-action-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 8px;
  color: #495057;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
}

.theme--dark .preview-action-btn {
  background: #2a2a2a;
  border-color: #424242;
  color: var(--v-mainBlack-base);
}

.preview-action-btn:hover {
  background: #e9ecef;
}

.theme--dark .preview-action-btn:hover {
  background: #3a3a3a;
}

/* Responsive adjustments for the new section */
@media (max-width: 960px) {
  .v-card {
    min-width: 90vw !important;
    max-width: 90vw !important;
    margin: 16px;
  }
  
  .v-col {
    padding: 8px;
  }
  
  .color-picker-item {
    padding: 16px;
    min-height: 120px;
    height: 120px;
  }
  
  .color-circle-large {
    width: 48px;
    height: 48px;
  }
  
  .upload-dropzone {
    padding: 32px 16px;
    min-height: 160px;
  }
  
  .upload-dropzone.has-image {
    min-height: 100px;
    padding: 16px;
  }
  
  .customization-card-title {
    padding: 16px 20px;
  }
  
  .preview-sample {
    max-width: 100%;
  }
  
  .preview-container {
    transform: scale(0.75);
    width: 133%;
    height: 133%;
  }
  
  .preview-main-content {
    flex-direction: column;
  }
  
  .preview-sidebar {
    flex-direction: row;
  }
}

@media (max-width: 600px) {
  .color-picker-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .color-picker-item {
    min-height: 160px;
    height: 160px;
  }
  
  .color-info {
    min-height: 60px;
  }
  
  .upload-title {
    font-size: 1.1rem;
  }
  
  .upload-dropzone {
    padding: 24px 12px;
    min-height: 140px;
  }
  
  .feature-categories {
    max-height: 300px;
  }
  
  .layout-preview {
    max-height: 400px;
  }
  
  .preview-container {
    transform: scale(0.65);
    width: 154%;
    height: 154%;
  }
}

/* Snackbar positioning */
.v-snackbar {
  z-index: 9999;
}

/* Color field background transition */
.v-text-field .v-input__slot {
  transition: background-color 0.3s ease;
}

/* Section spacing */
.mb-4 {
  margin-bottom: 24px !important;
}

.mb-6 {
  margin-bottom: 32px !important;
}

/* Enhanced overlay styling */
.v-overlay {
  z-index: 1000;
}

.v-tabs ::v-deep .v-tabs-bar {
  background: transparent !important;
}

/* UTC Tooltip Styling */
::v-deep .utc-tooltip-content {
  background-color: var(--v-mainGreen-base) !important;
  color: white !important;
  padding: 8px 12px !important;
  font-size: 13px !important;
  line-height: 1.4 !important;
  border-radius: 4px !important;
  opacity: 0.95 !important;
}

::v-deep .v-tooltip__content {
  pointer-events: auto !important;
}

/* Ensure white text in the tooltip */
::v-deep .utc-tooltip-content .white--text {
  color: white !important;
}
</style>
